import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import { Order, OrderStatus, OrderType, OrderAuthorization } from '../../../../../../types/order/order';
import React, { useContext } from 'react';
import { StockLocationRoleAssignmentContext } from '../../../../../../context/StockLocationRoleAssignmentContext';
import { t, translate } from '../../../../../../types/translation/Translator';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { CustomFieldEntitySubType, CustomFieldEntityType } from '../../../../../../types/custom_field/customField';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { BusinessCentral } from '../../../../../../types/common/integration/bc';
import { SAP } from '../../../../../../types/common/integration/sap';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { OrderUtil } from '../../../../../../types/order';
import { AddButton, Button, ButtonVariant } from '../../../../../components/Button';
import GenerateBarcodeButton from '../../../../../components/Button/templates/GenerateBarcodeButton';
import { BarcodeExportEntityType } from '../../../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { ProductTransactionContext } from '../../../../../../context/ProductTransactionContext';
import { ProductTransactionStatus } from '../../../../../../types/productTransaction';
import GeneratePdfButton from '../../../../../components/Button/templates/GeneratePdfButton';
import { PdfTemplateEntityType } from '../../../../../../types/pdf_template';
import { Unilever } from '../../../../../../types/customer/DHL/Unilever/Unilever';
import UnileverImportScanButton from '../../../../../../types/customer/DHL/Unilever/components/UnileverImportScanButton';

interface UpdateOrderPaneFooterProps {
  order: Order;
  loading: {
    update: boolean;
    cancel: boolean;
    complete: boolean;
  };
  actions: {
    onBack: () => void;
    onComplete: () => void;
    onCancel: () => void;
    onAssigned: () => void;
    onAddProduct: () => void;
    onUpdate: () => void;
    onManualCustomField: () => void;
  };
}

export default function UpdateOrderPaneFooter({ order, loading, actions }: UpdateOrderPaneFooterProps) {
  const { currentCompany } = useContext(CompanyContext);
  const featureToggles = currentCompany.settings.featureToggles.orders;

  const { productTransactions } = useContext(ProductTransactionContext);
  const orderTransactions = [...productTransactions.values()].filter(pt => pt.parentId === order.id);

  const { customFields } = useContext(CustomFieldContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);

  const isDetail = window.location.pathname.endsWith('/detail');
  const isInfo = window.location.pathname.endsWith('/info');
  const isUser = window.location.pathname.endsWith('/users');

  const authorization = new OrderAuthorization();
  const entitySubtype =
    order.type === OrderType.inbound ? CustomFieldEntitySubType.inbound : CustomFieldEntitySubType.outbound;

  const customFieldItems = [...customFields.values()].filter(
    v => v.entityType === CustomFieldEntityType.order && (!v.entitySubtype || v.entitySubtype === entitySubtype),
  );

  const hasManualCustomFields = customFieldItems.some(cf => cf.manual);

  const alwaysUpdatableCustomFields = [...customFields.values()].filter(
    v =>
      v.entityType === CustomFieldEntityType.order &&
      (!v.entitySubtype || v.entitySubtype === entitySubtype) &&
      v.generated &&
      !v.onCreate,
  );

  const completeText = t(
    (() => {
      if (!currentCompany.settings.featureToggles.orders.picking || !Order.hasPicking(order)) {
        return TranslationKey.completeOrder;
      }

      if (order.status === OrderStatus.pickingComplete) {
        if (order.externalIdentifier?.settingsId == OrderUtil.SAP_IDENTIFIER) {
          return TranslationKey.issueGoods;
        } else {
          return TranslationKey.completeOrder;
        }
      } else {
        return TranslationKey.completePicking;
      }
    })(),
  ).singular.upper;

  const isCompleted = [OrderStatus.complete, OrderStatus.cancelled].includes(order.status);

  const canProcess = hasStockLocationRole(
    order.companyId,
    OrderUtil.determineStockLocation(order),
    authorization.forProcess(),
  );
  const canUpdate = hasStockLocationRole(
    order.companyId,
    OrderUtil.determineStockLocation(order),
    authorization.forUpdate(),
  );
  const canAddProducts =
    (order.type === OrderType.inbound && featureToggles.editProductsOnInbound) ||
    (order.type === OrderType.outbound && featureToggles.editProductsOnOutbound) ||
    (order.type === OrderType.disposal && featureToggles.editProductsOnDisposal) ||
    (order.type === OrderType.move && featureToggles.editProductsOnMove);

  const isBusinessCentral =
    BusinessCentral.isPurchaseOrder(order) ||
    BusinessCentral.isItemJournal(order) ||
    BusinessCentral.isJob(order) ||
    BusinessCentral.isElterosInvoice(order);
  const isSAP = SAP.isInboundDelivery(order) || SAP.isOutboundDelivery(order);

  const showCompleteButton =
    canProcess &&
    !isCompleted &&
    !isBusinessCentral &&
    (!isSAP || SAP.isOutboundDelivery(order)) &&
    !Unilever.companyIds.includes(currentCompany.companyId);

  const showCancelButton = canUpdate && !isCompleted && !BusinessCentral.isPurchaseOrder(order);
  const showAssignedButton = canUpdate && isUser && !isCompleted;
  const showAddProductsButton = canUpdate && canAddProducts && !isCompleted && !isBusinessCentral && !isSAP;
  const showUpdateButton =
    (canUpdate && isInfo && customFieldItems.length) || (alwaysUpdatableCustomFields.length && isInfo);
  const showManualCustomFieldButton = canUpdate && isInfo && hasManualCustomFields;
  const showGenerateBarcodeButton =
    isDetail && orderTransactions.some(pt => pt.status === ProductTransactionStatus.processed);
  const showGeneratePdfButton = isDetail && order.status === OrderStatus.complete;

  const backButton = (
    <Button
      variant={ButtonVariant.secondary}
      onClick={actions.onBack}
      text={t(TranslationKey.back).singular.label}
      disabled={loading.update || loading.cancel}
    />
  );

  let completeButton = null;
  if (showCompleteButton) {
    completeButton = (
      <Button
        variant={ButtonVariant.primary}
        loading={loading.complete}
        onClick={actions.onComplete}
        text={completeText}
        disabled={loading.update || loading.cancel}
      />
    );
  }

  let cancelButton = null;
  if (showCancelButton) {
    cancelButton = (
      <Button
        variant={ButtonVariant.secondary}
        loading={loading.cancel}
        onClick={actions.onCancel}
        text={t(TranslationKey.cancelOrder).singular.label}
        disabled={loading.update}
      />
    );
  }

  let assignedButton = null;
  if (showAssignedButton) {
    assignedButton = (
      <Button
        disabled={loading.update || loading.cancel || loading.complete}
        onClick={actions.onAssigned}
        text={t(TranslationKey.reassignUsers).singular.label}
        leading={<GroupAddIcon />}
      />
    );
  }

  let addProductsButton = null;
  if (showAddProductsButton) {
    addProductsButton = (
      <AddButton
        key={'addProductsButton'}
        disabled={loading.cancel || loading.complete || loading.update}
        onClick={actions.onAddProduct}
        text={t(TranslationKey.addProducts).singular.label}
      />
    );
  }

  let updateButton = null;
  if (showUpdateButton) {
    updateButton = (
      <Button
        key={'updateButton'}
        variant={ButtonVariant.primary}
        loading={loading.update}
        disabled={loading.cancel || loading.complete}
        onClick={actions.onUpdate}
        text={t(TranslationKey.update).singular.label}
      />
    );
  }

  let addCustomFieldButton = null;
  if (showManualCustomFieldButton) {
    addCustomFieldButton = (
      <AddButton
        key={'addCustomFieldButton'}
        loading={loading.update}
        disabled={loading.cancel || loading.complete}
        onClick={actions.onManualCustomField}
        text={t(TranslationKey.addCustomField).singular.label}
      />
    );
  }

  let generateBarcodeButton = null;
  if (showGenerateBarcodeButton) {
    generateBarcodeButton = (
      <GenerateBarcodeButton
        text={translate({ key: TranslationKey.generateBarcode, count: 'plural' })}
        entityType={BarcodeExportEntityType.order}
        entityIds={[order.id]}
      />
    );
  }

  let generatePdfButton = null;
  if (showGeneratePdfButton) {
    generatePdfButton = (
      <GeneratePdfButton
        entityType={PdfTemplateEntityType.order}
        entitySubtype={OrderUtil.orderTypeToPdfTemplateEntitySubtype(order.type)}
        entityId={order.id}
      />
    );
  }

  let unileverImportButton = null;
  if (
    currentCompany.companyId === Unilever.qaCompanyId &&
    order.status !== OrderStatus.complete &&
    order.status !== OrderStatus.cancelled
  ) {
    unileverImportButton = <UnileverImportScanButton order={order} />;
  }

  return (
    <div>
      <div className='flex justify-between'>
        <div className='flex gap-2'>{[cancelButton, completeButton]}</div>
        <div className='flex gap-2'>
          {[
            unileverImportButton,
            generatePdfButton,
            generateBarcodeButton,
            assignedButton,
            addProductsButton,
            addCustomFieldButton,
            backButton,
            updateButton,
          ]}
        </div>
      </div>
    </div>
  );
}
